import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
config.autoAddCss = false;
import './App.css';

import { Navigate, Route, Routes } from 'react-router';
import { useEffect, lazy } from 'react';
import { AppDetailLayout, MainLayout } from './components/layouts';

// Lazy load page components for code splitting
const Dashboard = lazy(() => import('./components/pages/Dashboard/Dashboard'));
const MyTasks = lazy(() => import('./components/pages/MyTasks/MyTasks'));
const AppOverview = lazy(() => import('./components/pages/AppOverview/AppOverview'));
const AppDetailOverview = lazy(() => import('./components/pages/AppDetailOverview/AppDetailOverview'));
const AppDetailCredentialList = lazy(
  () => import('./components/pages/AppDetailCredentialList/AppDetailCredentialList')
);
const AppDetailOwners = lazy(() => import('./components/pages/AppDetailOwners/AppDetailOwners'));
const AppDetailPolicies = lazy(() => import('./components/pages/AppDetailPolicies/AppDetailPolicies'));
const AppDetailSettings = lazy(() => import('./components/pages/AppDetailSettings/AppDetailSettings'));

import { FilterContextProvider } from './lib/state';
import { CredentialType, ResourceType } from './api/app.types';
import { ExpirationType } from './lib/model';
import { TaskDetailPanelProvider } from './components/panels';
import { QueryStringService } from '@easylife365/react/auth';
import { useIsAppOnboarded } from './api/app.api';
import { PageNotFound } from '@easylife365/react';
import { useLicenseInfo } from './lib/hooks';

const App: React.FC = () => {
  const { mutate: checkIsAppOnboarded } = useIsAppOnboarded();

  const { isProfessional } = useLicenseInfo();

  // tId is passed in the Teams client
  const tenantId = QueryStringService.getInstance().getUrlParameterByName('tid');
  useEffect(() => {
    if (!tenantId) return;

    checkIsAppOnboarded({ request: { tenantId } });
  }, [tenantId, checkIsAppOnboarded]);

  return (
    <FilterContextProvider>
      <TaskDetailPanelProvider>
        <Routes>
          <Route element={<MainLayout />}>
            <Route index path="dashboard" element={<Dashboard />} />
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="tasks" element={<MyTasks />} />
            <Route path="registeredApps" element={<AppOverview resourceType={ResourceType.AppRegistration} />} />
            <Route
              path="registeredApps/upcoming"
              element={
                <AppOverview
                  resourceType={ResourceType.AppRegistration}
                  expirationType={ExpirationType.AboutToExpire}
                />
              }
            />
            <Route
              path="registeredApps/expired"
              element={
                <AppOverview resourceType={ResourceType.AppRegistration} expirationType={ExpirationType.Expired} />
              }
            />
            {isProfessional && (
              <Route
                path="registeredApps/managed"
                element={
                  <AppOverview resourceType={ResourceType.AppRegistration} expirationType={ExpirationType.Managed} />
                }
              />
            )}
            <Route path="apps" element={<AppOverview resourceType={ResourceType.EnterpriseApplication} />} />
            <Route
              path="apps/upcoming"
              element={
                <AppOverview
                  resourceType={ResourceType.EnterpriseApplication}
                  expirationType={ExpirationType.AboutToExpire}
                />
              }
            />
            <Route
              path="apps/expired"
              element={
                <AppOverview
                  resourceType={ResourceType.EnterpriseApplication}
                  expirationType={ExpirationType.Expired}
                />
              }
            />
            {isProfessional && (
              <Route
                path="apps/managed"
                element={
                  <AppOverview
                    resourceType={ResourceType.EnterpriseApplication}
                    expirationType={ExpirationType.Managed}
                  />
                }
              />
            )}
          </Route>
          <Route element={<AppDetailLayout resourceType={ResourceType.AppRegistration} />}>
            <Route path="registeredApps/:id/overview" element={<AppDetailOverview />} />
            <Route path="registeredApps/:id/policies" element={<AppDetailPolicies />} />
            <Route
              path="registeredApps/:id/certificates"
              element={<AppDetailCredentialList type={CredentialType.Certificate} />}
            />
            <Route
              path="registeredApps/:id/secrets"
              element={<AppDetailCredentialList type={CredentialType.Secret} />}
            />
            <Route path="registeredApps/:id/owners" element={<AppDetailOwners />} />
            <Route path="registeredApps/:id/settings" element={<AppDetailSettings />} />
          </Route>
          <Route element={<AppDetailLayout resourceType={ResourceType.EnterpriseApplication} />}>
            <Route path="apps/:id/overview" element={<AppDetailOverview />} />
            <Route path="apps/:id/policies" element={<AppDetailPolicies />} />
            <Route
              path="apps/:id/certificates"
              element={<AppDetailCredentialList type={CredentialType.Certificate} />}
            />
            <Route path="apps/:id/owners" element={<AppDetailOwners />} />
            <Route path="apps/:id/settings" element={<AppDetailSettings />} />
          </Route>
          <Route index path="*" element={<PageNotFound />} />
        </Routes>
      </TaskDetailPanelProvider>
    </FilterContextProvider>
  );
};

export default App;
